import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ThreatRow {
  readonly threatId: string;
  readonly threatCode: string;
  readonly threatType: string;
  readonly detectionSource: string;
  readonly severity: string;
  readonly status: string;
  readonly alertGenerated: boolean;
  readonly occurrenceCount: number;
}

const TYPES = ['', 'BRUTE_FORCE', 'CREDENTIAL_STUFFING', 'UNAUTHORIZED_ACCESS', 'PRIVILEGE_ESCALATION', 'PRIVILEGE_ABUSE', 'API_ABUSE', 'DATA_LEAKAGE', 'DATA_EXFILTRATION', 'MALICIOUS_INTEGRATION', 'INSIDER_THREAT', 'SUSPICIOUS_ACTIVITY', 'CONFIGURATION_CHANGE', 'POLICY_VIOLATION', 'LOGIN_FAILURE'];
const STATUSES = ['', 'DETECTED', 'INVESTIGATING', 'CONFIRMED', 'MITIGATED', 'RESOLVED', 'FALSE_POSITIVE', 'ESCALATED'];

/**
 * M30 — Threat Monitoring Dashboard (§11 / §13 / §31). Live feed of detected
 * security threats with their type, detection source, severity, workflow status
 * and whether an alert was generated (BR-05: unauthorized-access attempts must
 * raise alerts). Confirmed threats can be escalated into incidents.
 */
export function M30ThreatMonitoringPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ThreatRow[]>([]);
  const [threatType, setThreatType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (threatType) params.set('threatType', threatType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ThreatRow[] }>(`/v1/security/threats${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, threatType, status]);

  return (
    <main aria-label="threat-monitoring">
      <h1>{tr('m30.threat.title')}</h1>
      <div className="filters">
        <label>
          {tr('m30.threat.filterType')}
          <select aria-label="filter-threat-type" value={threatType} onChange={(e) => setThreatType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m30.threat.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m30.threat.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m30.threat.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m30.threat.loading')}</p> : (
        <>
          <p aria-label="threat-count">{tr('m30.threat.count', { count: rows.length })}</p>
          <table aria-label="threat-table">
            <thead><tr><th>{tr('m30.threat.thCode')}</th><th>{tr('m30.threat.thType')}</th><th>{tr('m30.threat.thSource')}</th><th>{tr('m30.threat.thSeverity')}</th><th>{tr('m30.threat.thStatus')}</th><th>{tr('m30.threat.thAlert')}</th><th>{tr('m30.threat.thOccurrences')}</th></tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.threatId}>
                  <td>{t.threatCode}</td><td>{t.threatType}</td><td>{t.detectionSource}</td>
                  <td>{t.severity}</td><td>{t.status}</td>
                  <td>{t.alertGenerated ? tr('m30.threat.alerted') : tr('m30.threat.alertNone')}</td><td>{t.occurrenceCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
